import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../firebase/config';

const COLLECTION_NAME = 'parrot-services';

export const ServiceStatus = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
};

export class ServicesService {
  
  // Create a new service
  static async create(providerId, serviceData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...serviceData,
        providerId,
        status: serviceData.status || ServiceStatus.DRAFT,
        rating: 0,
        reviews: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { id: docRef.id, ...serviceData };
    } catch (error) {
      console.error('Error creating service:', error);
      throw error;
    }
  }

  // Get a single service by ID
  static async getById(serviceId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, serviceId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting service:', error);
      throw error;
    }
  }

  // Get all services by provider ID
  static async getByProviderId(providerId, includeStatus = null) {
    try {
      let q;
      if (includeStatus) {
        q = query(
          collection(db, COLLECTION_NAME),
          where('providerId', '==', providerId),
          where('status', '==', includeStatus),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(
          collection(db, COLLECTION_NAME),
          where('providerId', '==', providerId),
          orderBy('createdAt', 'desc')
        );
      }
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting provider services:', error);
      throw error;
    }
  }

  // Get all published services (for homepage)
  static async getPublished(categoryId = null, limit = 50) {
    try {
      let q;
      if (categoryId && categoryId !== 'all') {
        q = query(
          collection(db, COLLECTION_NAME),
          where('status', '==', ServiceStatus.PUBLISHED),
          where('categoryId', '==', categoryId),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(
          collection(db, COLLECTION_NAME),
          where('status', '==', ServiceStatus.PUBLISHED),
          orderBy('createdAt', 'desc')
        );
      }
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting published services:', error);
      throw error;
    }
  }

  // Update a service
  static async update(serviceId, updateData) {
    try {
      const docRef = doc(db, COLLECTION_NAME, serviceId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });
      return { id: serviceId, ...updateData };
    } catch (error) {
      console.error('Error updating service:', error);
      throw error;
    }
  }

  // Publish a service
  static async publish(serviceId) {
    return this.update(serviceId, { status: ServiceStatus.PUBLISHED });
  }

  // Unpublish (set to draft)
  static async unpublish(serviceId) {
    return this.update(serviceId, { status: ServiceStatus.DRAFT });
  }

  // Delete a service
  static async delete(serviceId) {
    try {
      const service = await this.getById(serviceId);
      if (service?.imageUrl) {
        await this.deleteImage(service.imageUrl);
      }
      const docRef = doc(db, COLLECTION_NAME, serviceId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting service:', error);
      throw error;
    }
  }

  // Upload service image
  static async uploadImage(providerId, serviceId, file) {
    try {
      const fileExtension = file.name.split('.').pop();
      const fileName = `${serviceId}_${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, `parrot-services/${providerId}/${fileName}`);
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  // Delete service image
  static async deleteImage(imageUrl) {
    try {
      if (!imageUrl || !imageUrl.includes('firebase')) return;
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    } catch (error) {
      console.warn('Error deleting image:', error);
    }
  }

  // Search services
  static async search(searchTerm, categoryId = null) {
    try {
      const services = await this.getPublished(categoryId);
      const term = searchTerm.toLowerCase();
      return services.filter(service => 
        service.title?.toLowerCase().includes(term) ||
        service.description?.toLowerCase().includes(term) ||
        service.tags?.some(tag => tag.toLowerCase().includes(term))
      );
    } catch (error) {
      console.error('Error searching services:', error);
      throw error;
    }
  }

  // Batch update multiple services
  static async batchUpdateStatus(serviceIds, status) {
    try {
      const batch = writeBatch(db);
      serviceIds.forEach(id => {
        const docRef = doc(db, COLLECTION_NAME, id);
        batch.update(docRef, { status, updatedAt: serverTimestamp() });
      });
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error batch updating services:', error);
      throw error;
    }
  }
}

export default ServicesService;